import {
  Text,
  NextImage,
  TextField,
  LinkField,
  ImageField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import useHelpers from 'src/hooks/helpers';
import AbrdnButton from 'components/Generic/Buttons/AbrdnButton';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import classNames from 'classnames';
import styles from './Showcase.module.scss';

type ShowcaseItem = {
  id: string;
  fields: {
    title: TextField;
    description: TextField;
    image: ImageField;
  };
};

type Fields = {
  title: TextField;
  description: TextField;
  cta: LinkField;
  items: ShowcaseItem[];
};

type ShowcaseProps = ComponentProps & {
  fields: Fields;
  rendering: {
    uid: string;
    params?: {
      anchorId?: string;
      background?: string;
    };
  };
};

const Showcase = ({ fields, rendering }: ShowcaseProps): JSX.Element => {
  const { anchorId, backgroundClass } = useHelpers(rendering);
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor: boolean | undefined = sitecoreContext.pageEditing;

  const isValueExists = (field: LinkField) => {
    return isExperienceEditor || field?.value?.text || field?.value?.href;
  };

  return (
    <section
      id={anchorId}
      data-e2e-component-uid={rendering.uid}
      className={classNames(backgroundClass, styles.showCase, 'default-section-padding')}
    >
      <div className="container">
        <div className="row d-flex">
          <div className="col-12 col-md-9 col-xl-5 mb-5 mb-md-8 mb-xl-0">
            {(fields.title || isExperienceEditor) && <Text tag="h2" field={fields.title} />}
            {(fields.description || isExperienceEditor) && (
              <Text tag="p" field={fields.description} className="mt-3 text-grey" />
            )}
            {isValueExists(fields.cta) && (
              <AbrdnButton
                linkField={fields.cta}
                replaceWithSlot={false}
                showExternalIcon={false}
                buttonType="primary"
                className="mt-5 text-grey"
              >
                <em className="bi-arrow-right ms-1" aria-hidden="true" />
              </AbrdnButton>
            )}
          </div>
          <div className="col-xl-1" />
          <div className="col-12 col-md-9 col-xl-5">
            {fields.items?.map((item, i) => (
              <div
                key={item.id}
                className={classNames('d-flex w-100 py-5', styles.showcaseItemsContainer, {
                  'align-items-center': !(item.fields.description && item.fields.description.value),
                  'pt-0': i === 0,
                  'pb-0': i === fields.items.length - 1,
                })}
              >
                {(item.fields.image || isExperienceEditor) && (
                  <NextImage
                    field={item.fields.image}
                    width={64}
                    height={64}
                    style={{
                      objectFit: 'contain',
                    }}
                    unoptimized
                  />
                )}
                <div className="d-flex flex-column ms-3">
                  {(item.fields.title || isExperienceEditor) && (
                    <Text tag="h3" field={item.fields.title} />
                  )}
                  {(item.fields.description || isExperienceEditor) && (
                    <Text
                      tag="p"
                      field={item.fields.description}
                      className={classNames(
                        'body-default text-grey',
                        styles.showcaseItemDescription
                      )}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Showcase;
